package com.alpha.showcase.common.ui.source

import com.alpha.showcase.common.networkfile.storage.StorageSources
import com.alpha.showcase.common.networkfile.storage.remote.OAuthRcloneApi
import com.alpha.showcase.common.networkfile.storage.remote.RcloneRemoteApi
import com.alpha.showcase.common.networkfile.storage.remote.RemoteApi
import com.alpha.showcase.common.repo.SourceListRepo
import com.alpha.showcase.common.sync.ConfigSync
import com.alpha.showcase.common.sync.ConfigSyncDomains
import com.alpha.showcase.common.ui.vm.BaseViewModel
import com.alpha.showcase.common.ui.vm.UiState
import com.alpha.showcase.common.ui.vm.succeeded
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import com.alpha.showcase.common.storage.objectStoreOf
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

open class SourceViewModel: BaseViewModel() {

  companion object: SourceViewModel()

  private val sourcesRepo = SourceListRepo()
  private val _sourceListStateFlow = MutableStateFlow<UiState<StorageSources>>(UiState.Loading)
  open val sourceListStateFlow = _sourceListStateFlow as StateFlow<UiState<StorageSources>>
  private val feedbackStore = objectStoreOf<SourceAdditionFeedback>("source_addition_feedback")
  private val feedbackMutex = Mutex()
  private val _additionFeedback = MutableStateFlow(SourceAdditionFeedback())
  internal val additionFeedback = _additionFeedback.asStateFlow()

  init {
    viewModelScope.launch {
      getSourceList()
      updateAdditionFeedback { it }
    }
  }

  init {
    viewModelScope.launch {
      ConfigSync.changes.filter { it == ConfigSyncDomains.SOURCES }.collect { getSourceList() }
    }
  }

  private suspend fun getSourceList(){
    try {
      _sourceListStateFlow.emit(UiState.Content(sourcesRepo.getSources()))
    } catch (error: CancellationException) {
      throw error
    } catch (error: Exception) {
      error.printStackTrace()
      _sourceListStateFlow.emit(UiState.Error(error.message))
    }
  }

  suspend fun addSourceList(remoteApi: RemoteApi, reportSuccess: Boolean = true): Boolean{
    val result = sourcesRepo.saveSource(remoteApi)
    if (result){
      val storageSources = sourcesRepo.getSources()
      _sourceListStateFlow.emit(UiState.Content(storageSources))
      if (reportSuccess) reportSourceAdded(remoteApi)
    }
    return result
  }

  suspend fun reportSourceAdded(remoteApi: RemoteApi) {
    updateAdditionFeedback { it.added(remoteApi.name) }
  }

  suspend fun markSourceOpened(remoteApi: RemoteApi) {
    updateAdditionFeedback { it.opened(remoteApi.name) }
  }

  internal suspend fun consumeAdditionCelebration() {
    updateAdditionFeedback { it.celebrated() }
  }

  private suspend fun updateAdditionFeedback(transform: (SourceAdditionFeedback) -> SourceAdditionFeedback) {
    try {
      feedbackMutex.withLock {
        val previous = feedbackStore.get() ?: SourceAdditionFeedback()
        val updated = transform(previous)
        if (updated != previous) feedbackStore.set(updated)
        _additionFeedback.value = updated
      }
    } catch (error: CancellationException) {
      throw error
    } catch (error: Exception) {
      // Feedback persistence must not turn a successfully saved source into a failed add.
      error.printStackTrace()
    }
  }

  suspend fun replaceSourceList(previous: RemoteApi, replacement: RemoteApi): Boolean {
    val result = sourcesRepo.replaceSource(previous, replacement)
    if (result) {
      _sourceListStateFlow.emit(UiState.Content(sourcesRepo.getSources()))
      updateAdditionFeedback { it.renamed(previous.name, replacement.name) }
    }
    return result
  }

  suspend fun deleteSource(remoteApi: RemoteApi): Boolean{
    val result = sourcesRepo.deleteSource(remoteApi)
    val storageSources = sourcesRepo.getSources()
    _sourceListStateFlow.emit(UiState.Content(storageSources))
    if (result) markSourceOpened(remoteApi)
    return result
  }

  fun getSource(name: String): RemoteApi?{
    if (_sourceListStateFlow.value is UiState.Content){
      return (_sourceListStateFlow.value as UiState.Content<StorageSources>).data.sources.find { it.name == name }
    }
    return null
  }


  fun checkDuplicateName(name: String): Boolean {
    sourceListStateFlow.value.let {
      if (it is UiState.Content && it.succeeded) {
        it.data.sources.forEach {source ->
          if (source.name == name) {
            return false
          }
        }
      }
    }
    return true
  }

  fun onStartPlay(remoteApi: RemoteApi){

  }

  suspend fun checkConnection(remoteApi: RemoteApi): Result<Any> =
    sourcesRepo.checkConnection(remoteApi)

  suspend fun getFilesItemList(remoteApi: RcloneRemoteApi, path: String): Result<Any> =
    sourcesRepo.getSourceFileDirItems(remoteApi, path)


}
